import axios from "axios";
import { atom, type Getter, type Setter } from "jotai";
import { atomWithQuery } from "jotai/query";
import { PredefinedLEZsLegend } from "src/components/legend-item/PredefinedLEZsLegend";
import { ScenarioStreetsWithBarrierLegend } from "src/components/legend-item/ScenarioStreetsWithBarrierLegend";
import { PredefinedLEZsOnMap } from "src/components/on-map/PredefinedLEZsOnMap";
import { ScenarioStreetsWithBarrierOverlayer } from "src/components/overlayers/ScenarioStreetsWithBarrierOverlayer";
import {
	GROUPS,
	getRunYearStaticOverlayers,
	staticOverlayers,
} from "src/config/overlayers";
import type { RunYearInfo } from "src/hooks/runYearHooks";
import { fetchExtraLayers } from "src/hooks/useExtraLayers";
import { queryFn as queryResidentialMaps } from "src/hooks/useResidentialMaps";
import { baseDataKeys } from "src/query-keys";
import type {
	BackendPredefinedLEZ,
	ComponentOverlayer,
	Emission,
	LayerGroup,
	Overlayer,
	PredefinedLEZ,
	WMSOverlayer,
} from "src/types";
import type { ExtraLayer } from "src/types/extra-layers";
import {
	addOrUpdateInList,
	deserializeBackendLayer,
	extraLayerToOverlayer,
	filterActive,
	residentialMapToOverlayer,
	residentialTotalMapToOverlayer,
} from "src/utils/layer-manager.utils";
import wkx from "wkx";

export const overlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>(
	(get: Getter) => {
		const activeOverlayers = get(activeOverlayersAtom);
		const scenarioEmissionLayers = get(scenarioEmissionsDerivedAtom);
		const residentialMaps = get(residentialmapsDerivedAtom);
		const extraLayers = get(extraLayersAtom);
		const atmoStreetLayers = get(atmoStreetLayersDerivedAtom);
		const stationLayers = get(stationLayersAtom);
		const predefinedLEZs = get(predefinedLEZsDerivedAtom);
		const runYearStaticOverlayers = get(runYearStaticOverlayersDerivedAtom);
		const scenarioStreetsWithBarrierLayers = get(
			scenarioStreetsWithBarrierLayersDerivedAtom,
		);
		const allOverlayers = [
			...staticOverlayers,
			...runYearStaticOverlayers,
			...predefinedLEZs,
			...stationLayers,
			...atmoStreetLayers,
			...scenarioEmissionLayers,
			...(residentialMaps ? residentialMaps : []),
			...(extraLayers ? extraLayers : []),
			...(scenarioStreetsWithBarrierLayers
				? scenarioStreetsWithBarrierLayers
				: []),
		];

		return allOverlayers.map((layer) => {
			const result = activeOverlayers.find((a) => a.key === layer.key);

			if (result) return result;
			return layer;
		});
	},
);
export const selectedScenarioIdAtom = atom<string | undefined>(undefined);

export type SelectedRunYear = RunYearInfo & { cutoff?: number };

const selectedRunYearValueAtom = atom<SelectedRunYear | undefined>(undefined);

export const runYearAtom = atom(
	(get) => get(selectedRunYearValueAtom),
	(get, set, newRunYear: SelectedRunYear | undefined) => {
		const oldRunYear = get(selectedRunYearValueAtom);

		if (
			oldRunYear?.year === newRunYear?.year &&
			oldRunYear?.cutoff === newRunYear?.cutoff
		) {
			return;
		}
		const activeOverlayers = get(activeOverlayersAtom);
		const isRunYearChange = oldRunYear?.year !== newRunYear?.year;

		// Cutoff-only updates keep run-year data layers and only refresh traffic/pointsources.
		if (oldRunYear && newRunYear && !isRunYearChange) {
			const trafficActive =
				activeOverlayers.find((l) => l.key === "traffic")?.isActive ?? false;
			const filtered = activeOverlayers.filter((l) => l.key !== "traffic");
			const nextRunYearOverlayers = getRunYearStaticOverlayers(
				newRunYear.year,
				newRunYear.cutoff,
				newRunYear.cutoffList,
			);
			const nextTrafficLayer = nextRunYearOverlayers.find((l) => l.key === "traffic");
			if (!nextTrafficLayer) {
				set(selectedRunYearValueAtom, newRunYear);
				return;
			}
			set(activeOverlayersAtom, [
				...filtered,
				{ ...nextTrafficLayer, isActive: trafficActive },
			]);
			set(selectedRunYearValueAtom, newRunYear);
			return;
		}

		// Run-year changes clear all run-year-dependent layers.
		if (oldRunYear !== undefined) {
			const filtered = activeOverlayers.filter((overlayer) => {
				if (overlayer.group.name === GROUPS.residentialMaps.name) return false;
				if (overlayer.group.name === GROUPS.atmostreet.name) return false;
				if (overlayer.key === "predefinedLEZs") return false;
				if (overlayer.key === "traffic") return false;
				if (overlayer.key === "pointsources") return false;
				return true;
			});
			set(activeOverlayersAtom, filtered);
		}

		set(selectedRunYearValueAtom, newRunYear);
		if (!newRunYear) return;

		const runYearOverlayers = getRunYearStaticOverlayers(
			newRunYear.year,
			newRunYear.cutoff,
			newRunYear.cutoffList,
		);
		const currentActiveOverlayers = get(activeOverlayersAtom);

		for (const overlayer of runYearOverlayers) {
			const exists = currentActiveOverlayers.find(
				(a) => a.key === overlayer.key,
			);
			if (!exists) {
				set(activeOverlayersAtom, [
					...get(activeOverlayersAtom),
					{ ...overlayer, isActive: true },
				]);
			}
		}
	},
);

export const runYearStaticOverlayersDerivedAtom = atom((get) => {
	const runYear = get(runYearAtom);
	if (!runYear) return [];
	return getRunYearStaticOverlayers(
		runYear.year,
		runYear.cutoff,
		runYear.cutoffList,
	);
});

export const scenarioEmissionsDerivedAtom = atom((get) => {
	const scenarioId = get(selectedScenarioIdAtom);
	if (!scenarioId) return [];

	return get(scenarioEmissionsAtom(scenarioId));
});

export const residentialmapsDerivedAtom = atom((get) => {
	const runYear = get(runYearAtom);
	if (!runYear) return [];

	return get(residentialMapsAtom(runYear.year));
});

export const atmoStreetLayersDerivedAtom = atom((get) => {
	const runYear = get(runYearAtom);
	if (!runYear) return [];

	return get(atmoStreetLayersAtom(runYear.year));
});

const predefinedLEZsDataAtom = (runYear: number) =>
	atomWithQuery(() => ({
		queryKey: baseDataKeys.predefinedLEZs(runYear),
		queryFn: async () => {
			if (!runYear) return [];
			const { data } = await axios.get<BackendPredefinedLEZ[]>(
				`/planning-api/genericzones/${runYear}`,
			);
			return data.map((item) => ({
				...item,
				geom: wkx.Geometry.parse(item.geom_frontendSrid).toGeoJSON(),
			})) as PredefinedLEZ[];
		},
		staleTime: 5 * 60 * 1000,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		gcTime: 10 * 60 * 1000,
	}));

const predefinedLEZsOverlayerDerivedAtom = (runYear: number) =>
	atom((get) => {
		const data = get(predefinedLEZsDataAtom(runYear));
		if (!data.length) return [] as Overlayer[];

		const layer: Overlayer = {
			key: "predefinedLEZs",
			translationKey: "layer.currentLowEmissionZones",
			isActive: false,
			opacity: 0.8,
			zIndex: 1,
			component: (props: { opacity: number }) => (
				<PredefinedLEZsOnMap predefinedLEZs={data} opacity={props.opacity} />
			),
			group: GROUPS.caseConfiguration,
			context: ["case-create"],
			layerManager: {
				showFrontLegend: true,
				showBackLegend: false,
				showInLayerManager: true,
			},
			legend: {
				legendType: "component",
				component: () => <PredefinedLEZsLegend />,
			},
		};
		return [layer];
	});

// and wire your existing derived atom to use the overlayer version:
export const predefinedLEZsDerivedAtom = atom((get) => {
	const runYear = get(runYearAtom);
	if (!runYear) return [];
	return get(predefinedLEZsOverlayerDerivedAtom(runYear.year));
});

export const stationLayersAtom = atomWithQuery(() => ({
	queryKey: baseDataKeys.stations,
	queryFn: async () => {
		const { data } = await axios.get<ExtraLayer[]>(
			"/planning-api/layers/stations",
		);
		return data.map((l) =>
			deserializeBackendLayer(
				l,
				"stations",
				GROUPS.stations,
				["case-create", "case-edit", "case-results"],
				l.unit,
			),
		);
	},
}));

const atmoStreetLayersAtom = (runYear: number) =>
	atomWithQuery(() => ({
		queryKey: baseDataKeys.atmoStreetLayers(runYear),
		queryFn: async () => {
			if (!runYear) return [];
			const { data } = await axios.get<ExtraLayer[]>(
				`/planning-api/layers/atmostreet/${runYear}`,
			);
			return data.map((l) =>
				deserializeBackendLayer(
					l,
					"atmostreet",
					GROUPS.atmostreet,
					["case-create", "case-edit", "case-results"],
					l.unit,
				),
			);
		},
	}));

export const scenarioEmissionsAtom = (scenarioId?: string) =>
	atomWithQuery<WMSOverlayer[], unknown>(() => ({
		queryKey: ["scenarioEmissions", scenarioId],
		queryFn: async () => {
			if (!scenarioId) return [];
			const { data } = await axios.get<Emission[]>(
				`/planning-api/scenarios/${scenarioId}/emissions`,
			);
			return data.map((emission) => ({
				key: `${emission.name}-${scenarioId}`,
				translationKey: `layer.scenario.${emission.type}`,
				url: "/geoserver/wms",
				layers:
					emission.type === "TRAFFIC"
						? "atmoplan:traffic"
						: "atmoplan:pointsources",
				opacity: 0.8,
				context: ["case-results"],
				isActive: false,
				group: GROUPS.emissions,
				zIndex: 0,
				params: {
					layers:
						emission.type === "TRAFFIC"
							? "atmoplan:traffic"
							: "atmoplan:pointsources",
					viewparams: `shapecollection_id:${emission.geoserverFilter}`,
					styles:
						emission.type === "TRAFFIC"
							? "atmoplan:traffic_scenario"
							: "atmoplan:pointsources_scenario",
				},
				layerManager: {
					showFrontLegend: true,
					showBackLegend: false,
					showInLayerManager: true,
				},
				legend: {
					legendType: "wms",
				},
			}));
		},
	}));

const extraLayersAtom = atomWithQuery(() => ({
	queryKey: baseDataKeys.extraLayers,
	queryFn: async () => {
		const data = await fetchExtraLayers();
		return data.map(extraLayerToOverlayer);
	},
}));

const residentialMapsAtom = (runYear: number) =>
	atomWithQuery(() => ({
		queryKey: baseDataKeys.residentialMaps(runYear),
		queryFn: async () => {
			if (!runYear) return [];
			const data = await queryResidentialMaps(runYear);
			return [
				// first total residential maps
				...data.totalMapList.map(residentialTotalMapToOverlayer),

				// second residential maps with substance, fuel, dwelling and appliance subdivision
				...data.mapList.map(residentialMapToOverlayer),
			];
		},
	}));

export const activeOverlayersAtom = atom<Array<Overlayer | ComponentOverlayer>>(
	filterActive(staticOverlayers),
);

export const scenarioStreetsWithBarrierLayersDerivedAtom = atom((get) => {
	const scenarioId = get(selectedScenarioIdAtom);
	if (!scenarioId) return [];
	return [get(scenarioStreetsWithBarrierLayersAtom())];
});
const scenarioStreetsWithBarrierLayersAtom = () =>
	atom<ComponentOverlayer>(() => ({
		key: "scenarioTrafficWithBarrier",
		translationKey: "layer.scenarioTrafficWithBarrier",
		isActive: false,
		opacity: 0.8,
		zIndex: 501,
		group: GROUPS.emissions,
		context: ["case-results"],
		layerManager: {
			showFrontLegend: true,
			showBackLegend: false,
			showInLayerManager: true,
		},
		legend: {
			legendType: "component",
			component: () => <ScenarioStreetsWithBarrierLegend />,
		},
		component: (props) => <ScenarioStreetsWithBarrierOverlayer {...props} />,
	}));

// add or update overlayer
export const addOrUpdateActiveOverlayerAtom = atom(
	null,
	(get: Getter, set: Setter, update: Overlayer) => {
		set(
			activeOverlayersAtom,
			addOrUpdateInList(get(activeOverlayersAtom), update),
		);
	},
);

// active readio means all other overlayers from the same group are disabled
export const updateActiveRadioOverlayerAtom = atom(
	null,
	(get: Getter, set: Setter, update: Overlayer) => {
		const activeOverlayers = get(activeOverlayersAtom);
		const filtered = activeOverlayers.filter(
			(overlayer) => overlayer.group.name !== update.group.name,
		);
		const newActiveOverlayers = [...filtered, { ...update, isActive: true }];
		set(activeOverlayersAtom, newActiveOverlayers);
	},
);

export const setLayerGroupToNoneAtom = atom(
	null,
	(get: Getter, set: Setter, group: LayerGroup) => {
		set(activeOverlayersAtom, () => {
			return get(activeOverlayersAtom).map((overlayer) => {
				if (overlayer.group.name === group.name)
					return { ...overlayer, isActive: false };
				return overlayer;
			});
		});
	},
);

// Remove all layers that depend on runYear (residential maps, atmostreet, predefined LEZs, traffic, pointsources)
// by setting the runYear to undefined
export const removeRunYearDependentLayersAtom = atom(
	null,
	(_get: Getter, set: Setter) => {
		// Setting runYear to undefined will trigger the cleanup logic in runYearAtom
		// which removes active layers and clears the derived atoms
		set(runYearAtom, undefined);
	},
);
